import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ClipboardListIcon, FileEditIcon, SearchIcon, FilterIcon, VideoIcon, CheckCircleIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const stepIcons = [<ClipboardListIcon className="w-6 h-6" data-id="element-1079" data-mp="1kb0uo8urxsil" />, <FileEditIcon className="w-6 h-6" data-id="element-1080" data-mp="1kc27oivcop2y" />, <SearchIcon className="w-6 h-6" data-id="element-1081" data-mp="2h0878rdf0rad" />, <FilterIcon className="w-6 h-6" data-id="element-1082" data-mp="2gz6t2e13roof" />, <VideoIcon className="w-6 h-6" data-id="element-1083" data-mp="2cymwpcocsp3c" />, <CheckCircleIcon className="w-6 h-6" data-id="element-1084" data-mp="2czfydz8ggoem" />];
const stepNumbers = ['01', '02', '03', '04', '05', '06'];
export function ProcessSteps() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" id="process" data-id="element-1085" data-mp="30q259n6q42n1">
      <div className="absolute inset-0 pointer-events-none" data-id="element-1086" data-mp="2vvec3b0ryk24">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-[500px] h-[500px] rounded-full bg-primary-50/30 blur-3xl hidden sm:block" data-id="element-1087" data-mp="2r2t0zlcve6ar" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-cyan-50/25 blur-3xl hidden sm:block" data-id="element-1088" data-mp="2w2fiw83n8apw" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-1089" data-mp="2m9zx0zp871zq">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-16" data-id="element-1090" data-mp="289ywxmgsbylb">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1091" data-mp="3tommpfs4hz17">
            {t.process.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1092" data-mp="1k0sl07e3b1v">
            {t.process.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1093" data-mp="15inyk1q1ts6o">
            {t.process.subtitle}
          </p>
        </motion.div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden lg:block" data-id="element-1094" data-mp="whpu5ibp7ink">
          <div className="relative" data-id="element-1095" data-mp="wirgwk22ttl4">
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: 'easeOut'
          }} className="absolute top-12 left-[8%] right-[8%] h-1 bg-gradient-to-r from-primary-300 via-primary-500 to-primary-300 origin-left rounded-full" data-id="element-1096" data-mp="wjsvgyiawy7l" />
            <div className="grid grid-cols-6 gap-4" data-id="element-1097" data-mp="284hhjodx36tf">
              {t.process.steps.map((step, index) => <motion.div key={stepNumbers[index]} initial={{
              opacity: 0,
              y: 30
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.2 + index * 0.12
            }} className="flex flex-col items-center text-center" data-id="element-1098" data-mp="2fhyv24yq9s5l" data-mpi={index}>
                  <div className="relative z-10" data-id="element-1099" data-mp="387fj6qfbemw0">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shadow-lg shadow-primary-500/25" data-id="element-1100" data-mp="3d34lh1ipyhw0">
                      {stepIcons[index]}
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-lg bg-white border-2 border-primary-200 flex items-center justify-center shadow-sm" data-id="element-1101" data-mp="3m2hw3xikxcib">
                      <span className="text-xs font-extrabold text-primary-600" data-id="element-1102" data-mp="34g0osufqyl1p">
                        {stepNumbers[index]}
                      </span>
                    </div>
                  </div>
                  <h3 className="mt-5 text-base font-bold text-navy-900" data-id="element-1103" data-mp="3d9docf8bgkwr">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-navy-500 leading-relaxed" data-id="element-1104" data-mp="2pl2sjqhh9bfc">
                    {step.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: vertical timeline */}
        <div className="lg:hidden space-y-0" data-id="element-1105" data-mp="2uh1el7fc2hcl">
          {t.process.steps.map((step, index) => <motion.div key={stepNumbers[index]} initial={{
          opacity: 0,
          x: -20
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="relative flex gap-5 pb-10 last:pb-0" data-id="element-1106" data-mp="epdj7gfywwvw" data-mpi={index}>
              {index < t.process.steps.length - 1 && <div className="absolute left-7 top-16 bottom-0 w-0.5 bg-gradient-to-b from-primary-400 to-primary-200" data-id="element-1107" data-mp="2bej20ap5ktrb" />}
              <div className="relative z-10 flex-shrink-0" data-id="element-1108" data-mp="21o68hszd1q91">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shadow-lg shadow-primary-500/20" data-id="element-1109" data-mp="21no4292jo5q1">
                  {stepIcons[index]}
                </div>
                <div className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-md bg-white border-2 border-primary-200 flex items-center justify-center" data-id="element-1110" data-mp="2z31zrwakto55">
                  <span className="text-[10px] font-extrabold text-primary-600" data-id="element-1111" data-mp="38ysfupojmn42">
                    {stepNumbers[index]}
                  </span>
                </div>
              </div>
              <div className="pt-1" data-id="element-1112" data-mp="2yztdtpya9pq1">
                <h3 className="text-lg font-bold text-navy-900" data-id="element-1113" data-mp="2z2xsjh9dk3uy">
                  {step.title}
                </h3>
                <p className="mt-1 text-sm text-navy-500 leading-relaxed" data-id="element-1114" data-mp="2q06ct59gulxi">
                  {step.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}